import React from 'react'
import EcddSectionAttachments from './EcddSectionAttachments'
import EcddSowOutreachPanel from './EcddSowOutreachPanel'
import { EcddFormsRail, FormsModuleGate } from '../../forms'

/** Default structured ECDD answers — persisted under buckets_snapshot.formAnswers */
export const DEFAULT_ECDD_FORM_ANSWERS = {
  triggerType: '',
  linkedAlertId: '',
  observer: '',
  obsDate: '',
  priorSmr: '',
  triggerDesc: '',
  triggerDate: '',
  kycCurrent: '',
  docType: '',
  docNum: '',
  docExp: '',
  verMethod: '',
  verResult: '',
  verDate: '',
  occupation: '',
  occConsistent: '',
  customerType: '',
  sofRequired: '',
  sofNoReason: '',
  sofMethod: '',
  sofDate: '',
  sofBy: '',
  sofCat: '',
  sofOtherDetail: '',
  sowCat: '',
  sofEvidence: '',
  /** Discrete evidence notes attached in-section when SoF enquiry = Yes */
  sofEvidenceItems: [],
  /** Verification findings pinned from Quick Checks / manual add */
  sofFindings: [],
  /** Uploaded supporting documents for SoF (names + blob metadata) */
  sofDocs: [],
  sofResp: '',
  sofAssess: '',
  satOverride: '',
  actFrom: '',
  actTo: '',
  actSummary: '',
  actConsistent: '',
  inconNotes: '',
  indicators: [],
  /** When inconsistent activity — explicit “no indicators” alternative to selecting indicator checkboxes */
  noIndicators: false,
  /** Uploaded supporting documents for activity analysis */
  actDocs: [],
  /** No gaming/payout activity available to review or link for this ECDD */
  noActivityToLink: false,
  engaged: '',
  engDate: '',
  engBy: '',
  engMode: '',
  tipDecl: false,
  tipDeclPostSmr: false,
  scrDate: '',
  scrResult: '',
  scrRef: '',
  /** Patron AML/PEP checks on file — satisfies screening gate when count > 0 */
  amlScreeningCount: 0,
  scrDisp: '',
  pepClass: '',
  coFindings: '',
  nextReview: '',
  cadence: '',
  newRisk: '',
}

const NO_INDICATORS_VALUE = 'no-indicators'

const INDICATORS = [
  { value: 'structuring', label: 'Structuring below thresholds', hint: 'Splitting transactions to avoid reporting triggers' },
  { value: 'minimal-play', label: 'Minimal play / cash-through', hint: 'Large insertions with little gaming before collect' },
  { value: 'sof-mismatch', label: 'Activity inconsistent with profile', hint: 'Spend inconsistent with occupation or SoF' },
  { value: 'refusal', label: 'Reluctance / refusal to provide information', hint: 'Evasive about identity, SoF or activity' },
  { value: 'thirdparty', label: 'Third-party involvement', hint: 'Playing or collecting on behalf of another' },
  { value: 'staff-influence', label: 'Attempts to influence staff', hint: 'Enquiries about reporting, tips/incentives' },
  {
    value: NO_INDICATORS_VALUE,
    label: 'No indicators',
    hint: 'No specific suspicious indicators apply to this activity.',
  },
]

export function hasNoIndicatorsSelection(a) {
  const list = Array.isArray(a?.indicators) ? a.indicators : []
  return !!a?.noIndicators || list.includes(NO_INDICATORS_VALUE)
}

export function hasSpecificIndicatorsSelected(a) {
  const list = Array.isArray(a?.indicators) ? a.indicators : []
  return list.some((x) => x && x !== NO_INDICATORS_VALUE)
}

function hasVal(v) {
  return v != null && String(v).trim() !== ''
}

export function isForeignPep(a) {
  return a.scrResult === 'hit' && a.scrDisp === 'true' && a.pepClass === 'foreign'
}

export function isPepPath(a) {
  return (
    a.triggerType === 'pep' ||
    (a.scrResult === 'hit' && a.scrDisp === 'true' && a.pepClass && a.pepClass !== 'notpep')
  )
}

export function occupationFlagged(a) {
  return ['student', 'unemployed', 'cash'].includes(a.occupation)
}

export function isLargePayoutTrigger(a) {
  return String(a?.triggerType || '').toLowerCase() === 'large_payout'
}

export function hasScreeningOnRecord(a) {
  const count = Number(a?.amlScreeningCount) || 0
  return count > 0 || hasVal(a?.scrRef)
}

/** Stamp ECDD screening fields from patron AML history (only fills empty fields by default). */
export function patchFormFromAmlHistory(current, { count, latest }, { fillEmptyOnly = true } = {}) {
  const patch = { amlScreeningCount: Number(count) || 0 }
  if (!latest || !patch.amlScreeningCount) return patch
  const id = latest.id != null ? String(latest.id) : ''
  const matches = Number(latest.result_count) || 0
  const created = latest.created_at
    ? new Date(latest.created_at).toISOString().slice(0, 10)
    : ''
  if (id && (!fillEmptyOnly || !hasVal(current?.scrRef))) patch.scrRef = id
  if (created && (!fillEmptyOnly || !hasVal(current?.scrDate))) patch.scrDate = created
  if (!fillEmptyOnly || !current?.scrResult) {
    patch.scrResult = matches > 0 ? 'hit' : 'clear'
  }
  return patch
}

/** Completeness gates for sign-off — mirrors PaySafeGo_ECDD_Form_Prototype.html */
export function evaluateEcddFormGates(a) {
  const t = a.triggerType
  const largePayout = isLargePayoutTrigger(a)
  const foreignPep = isForeignPep(a)
  const sofReq = foreignPep ? 'yes' : a.sofRequired
  const actMandatory = t === 'alert' || t === 'unusual' || t === 'risk' || largePayout
  const inconsistent = a.actConsistent === 'no'
  const noActivityToLink = !!a.noActivityToLink
  const noIndicators = hasNoIndicatorsSelection(a)
  const indicatorsSelected = hasSpecificIndicatorsSelected(a)
  const indicatorsResolved = noIndicators || indicatorsSelected
  const refused = a.sofResp === 'refused'
  const postSmr = t === 'postsmr'
  const hit = a.scrResult === 'hit'
  const occFlag = occupationFlagged(a)

  const triggerOk = largePayout
    ? !!t
    : !!t &&
      hasVal(a.triggerDesc) &&
      hasVal(a.triggerDate) &&
      (t !== 'alert' || hasVal(a.linkedAlertId)) &&
      (t !== 'manual' || (hasVal(a.observer) && hasVal(a.obsDate))) &&
      (t !== 'postsmr' || hasVal(a.priorSmr))

  const kycOk = largePayout
    ? true
    : !!a.kycCurrent &&
    (a.kycCurrent !== 'no' ||
      (hasVal(a.docType) &&
        hasVal(a.docNum) &&
        hasVal(a.docExp) &&
        hasVal(a.verMethod) &&
        hasVal(a.verResult) &&
        hasVal(a.verDate))) &&
    (!occFlag || !!a.occConsistent)

  let sofOk = false
  if (sofReq === 'no') sofOk = hasVal(a.sofNoReason)
  else if (sofReq === 'yes') {
    sofOk =
      hasVal(a.sofMethod) &&
      hasVal(a.sofDate) &&
      hasVal(a.sofBy) &&
      hasVal(a.sofCat) &&
      (a.sofCat !== 'other' || hasVal(a.sofOtherDetail)) &&
      !!a.sofResp &&
      !!a.sofAssess &&
      (!(refused && a.sofAssess === 'sat') || hasVal(a.satOverride))
  }

  const activityOk =
    !actMandatory && !inconsistent
      ? true
      : noActivityToLink
        ? true
        : hasVal(a.actSummary) && (!inconsistent || (hasVal(a.inconNotes) && indicatorsResolved))

  let engOk = false
  if (a.engaged === 'yes') {
    engOk = hasVal(a.engDate) && hasVal(a.engBy) && hasVal(a.engMode) && !!a.tipDecl
  } else if (a.engaged === 'no') engOk = true
  if (postSmr) engOk = engOk && !!a.tipDeclPostSmr

  const screenOk = hasScreeningOnRecord(a)
    ? true
    : hasVal(a.scrDate) &&
      !!a.scrResult &&
      (!hit || (!!a.scrDisp && (a.scrDisp !== 'true' || !!a.pepClass)))

  const gates = [
    {
      id: 'trigger',
      label: largePayout
        ? 'Trigger type recorded (large payout)'
        : 'Trigger recorded (type, description, date)',
      ok: triggerOk,
    },
  ]
  if (!largePayout) {
    gates.push({
      id: 'kyc',
      label: 'KYC status answered' + (a.kycCurrent === 'no' ? ' + re-verification complete' : ''),
      ok: kycOk,
    })
  }
  gates.push({ id: 'sof', label: 'SoF/SoW enquiry resolved (done, or non-requirement documented)', ok: sofOk })
  if (actMandatory || inconsistent) {
    gates.push({
      id: 'activity',
      label: noActivityToLink
        ? 'Activity analysis — none to link'
        : 'Activity analysis complete',
      ok: activityOk,
    })
  }
  gates.push({
    id: 'engage',
    label: 'Engagement answered' + (a.engaged === 'yes' || postSmr ? ' + tipping-off declared' : ''),
    ok: engOk,
  })
  gates.push({
    id: 'screen',
    label: hasScreeningOnRecord(a) && !hasVal(a.scrDate)
      ? 'Screening on record (AML/PEP history)'
      : 'Screening refresh recorded' + (hit ? ' + match dispositioned' : ''),
    ok: screenOk,
  })

  return {
    gates,
    allOk: gates.every((g) => g.ok),
    sofReq,
    foreignPep,
    actMandatory,
    inconsistent,
    noActivityToLink,
    indicatorsSelected,
    noIndicators,
    indicatorsResolved,
    refused,
    postSmr,
    hit,
    occFlag,
    largePayout,
    returnBlocked: sofReq === 'yes' && (a.sofAssess === 'unsat' || a.sofAssess === 'further'),
  }
}

/** Payload for POST /api/ecdd-approval-triggers/check from form + decision */
export function formAnswersToTriggerCheck(a, decision, riskScore) {
  const pepClass = a.pepClass || (a.triggerType === 'pep' ? 'foreign' : '')
  return {
    decision: decision || null,
    pep_type: pepClass === 'foreign' || pepClass === 'domestic' || pepClass === 'intorg' ? pepClass : null,
    sanctions_match: a.scrResult === 'hit' && a.scrDisp === 'true' && a.pepClass === 'notpep',
    is_pep: isPepPath(a),
    risk_score: riskScore != null ? Number(riskScore) : null,
    cdd_tier_change: null,
  }
}

function Chip({ name, value, checked, onChange, label, disabled, danger }) {
  return (
    <label
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border text-[13px] font-medium cursor-pointer select-none transition-colors ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      } ${
        checked
          ? danger
            ? 'border-red-500 bg-red-50 text-red-800 font-semibold'
            : 'border-psg-teal bg-[#0A516908] text-psg-teal font-semibold'
          : 'border-slate-200 bg-white text-slate-600'
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => !disabled && onChange(value)}
        className="accent-[#0A5169] m-0"
      />
      {label}
    </label>
  )
}

function Field({ label, required, hint, children }) {
  return (
    <div className="mb-4 last:mb-0">
      <label className="block text-[13px] font-semibold text-slate-600 mb-1.5">
        {label}
        {required ? <span className="text-red-600"> *</span> : null}
      </label>
      {children}
      {hint ? <p className="text-xs text-slate-400 mt-1 mb-0">{hint}</p> : null}
    </div>
  )
}

const inputClass =
  'w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:border-psg-teal disabled:bg-slate-100 disabled:text-slate-400'

function Cond({ open, children }) {
  if (!open) return null
  return (
    <div className="border-l-[3px] border-psg-teal pl-4 pr-2 py-3 my-3 bg-[#0A516905] rounded-r-lg">
      {children}
    </div>
  )
}

function Notice({ kind = 'info', children }) {
  const styles = {
    info: 'bg-[#0A516908] text-psg-teal border-[#0A516925]',
    warn: 'bg-amber-50 text-amber-800 border-amber-200',
    block: 'bg-red-50 text-red-700 border-red-200',
  }
  return (
    <div className={`rounded-lg px-3.5 py-2.5 text-[13px] font-medium border mb-3 ${styles[kind] || styles.info}`}>
      {children}
    </div>
  )
}

/**
 * Structured ECDD form sections (trigger → screening) + shared field helpers.
 * Parent owns formAnswers state and FormSection wrappers for numbering.
 */
export function EcddFormFields({ formAnswers: a, onChange, readOnly = false }) {
  const set = (patch) => {
    if (readOnly) return
    onChange(patch)
  }
  const setField = (key, value) => set({ [key]: value })
  const evalState = evaluateEcddFormGates(a)
  const {
    sofReq,
    foreignPep,
    actMandatory,
    inconsistent,
    indicatorsSelected,
    refused,
    postSmr,
    hit,
    occFlag,
  } = evalState

  const toggleIndicator = (value) => {
    if (readOnly) return
    const cur = Array.isArray(a.indicators) ? a.indicators : []
    setField(
      'indicators',
      cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]
    )
  }

  return (
    <div className="space-y-1" data-ecdd-form-eval={JSON.stringify({ sofReq, foreignPep })}>
      {/* Trigger fields — rendered inside parent FormSection */}
      <div data-section="trigger">
        <Field label="Trigger type" required>
          <div className="flex flex-wrap gap-2">
            {[
              ['alert', 'Rule / alert'],
              ['pep', 'PEP / screening hit'],
              ['large_payout', 'Large payout'],
              ['risk', 'High risk rating'],
              ['manual', 'Manual observation'],
              ['postsmr', 'Post-SMR'],
              ['unusual', 'Unusual / complex activity'],
            ].map(([v, l]) => (
              <Chip
                key={v}
                name="trig"
                value={v}
                label={l}
                checked={a.triggerType === v}
                disabled={readOnly}
                onChange={(val) => setField('triggerType', val)}
              />
            ))}
          </div>
        </Field>
        <Cond open={a.triggerType === 'alert'}>
          <Field label="Linked alert ID" required>
            <input className={inputClass} disabled={readOnly} value={a.linkedAlertId} onChange={(e) => setField('linkedAlertId', e.target.value)} placeholder="e.g. ALT-2026-0412" />
          </Field>
        </Cond>
        <Cond open={a.triggerType === 'manual'}>
          <Field label="Observer name & role" required>
            <input className={inputClass} disabled={readOnly} value={a.observer} onChange={(e) => setField('observer', e.target.value)} placeholder="Name — role" />
          </Field>
          <Field label="Observation date" required>
            <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.obsDate} onChange={(e) => setField('obsDate', e.target.value)} />
          </Field>
        </Cond>
        <Cond open={a.triggerType === 'postsmr'}>
          <Field label="Prior SMR reference" required>
            <input className={inputClass} disabled={readOnly} value={a.priorSmr} onChange={(e) => setField('priorSmr', e.target.value)} placeholder="SMR-YYYY-NNNN" />
          </Field>
        </Cond>
        {a.triggerType === 'large_payout' && (
          <Notice kind="info">
            Patron KYC and member/visitor classification were established at payout. Observer, trigger narrative, and trigger date are not required for this trigger type.
          </Notice>
        )}
        <Cond open={a.triggerType !== 'large_payout'}>
          <Field label="Trigger description" required hint="Locked narrative of why ECDD was opened.">
            <textarea className={`${inputClass} min-h-[72px] resize-y`} disabled={readOnly} value={a.triggerDesc} onChange={(e) => setField('triggerDesc', e.target.value)} placeholder="Describe what triggered this ECDD." />
          </Field>
          <Field label="Trigger date" required>
            <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.triggerDate} onChange={(e) => setField('triggerDate', e.target.value)} />
          </Field>
        </Cond>
      </div>
    </div>
  )
}

/** Section bodies split for parent numbering */
export function EcddTriggerFields(props) {
  return <EcddFormFields {...props} />
}

export function EcddKycFields({ formAnswers: a, onChange, readOnly }) {
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const occFlag = occupationFlagged(a)
  return (
    <>
      <Field label="Is the patron's KYC current?" required>
        <div className="flex flex-wrap gap-2">
          <Chip name="kyc" value="yes" label="Yes" checked={a.kycCurrent === 'yes'} disabled={readOnly} onChange={(v) => setField('kycCurrent', v)} />
          <Chip name="kyc" value="no" label="No" checked={a.kycCurrent === 'no'} disabled={readOnly} onChange={(v) => setField('kycCurrent', v)} />
        </div>
      </Field>
      {a.kycCurrent === 'yes' && (
        <Notice kind="info">KYC is current — re-verification fields stay closed. No supporting checklist for this section.</Notice>
      )}
      <Cond open={a.kycCurrent === 'no'}>
        <Field label="Document type" required>
          <select className={inputClass} disabled={readOnly} value={a.docType} onChange={(e) => setField('docType', e.target.value)}>
            <option value="">Select…</option>
            <option>Driver licence</option>
            <option>Passport</option>
            <option>Photo card</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Document number" required>
          <input className={inputClass} disabled={readOnly} value={a.docNum} onChange={(e) => setField('docNum', e.target.value)} />
        </Field>
        <Field label="Expiry" required>
          <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.docExp} onChange={(e) => setField('docExp', e.target.value)} />
        </Field>
        <Field label="Verification method" required>
          <select className={inputClass} disabled={readOnly} value={a.verMethod} onChange={(e) => setField('verMethod', e.target.value)}>
            <option value="">Select…</option>
            <option>Sight-and-record (in person)</option>
            <option>Electronic verification (DVS)</option>
          </select>
        </Field>
        <Field label="Result" required>
          <select className={inputClass} disabled={readOnly} value={a.verResult} onChange={(e) => setField('verResult', e.target.value)}>
            <option value="">Select…</option>
            <option>Verified</option>
            <option>Not verified</option>
          </select>
        </Field>
        <Field label="Verification date" required hint="Structured details only — no document images retained.">
          <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.verDate} onChange={(e) => setField('verDate', e.target.value)} />
        </Field>
      </Cond>
      <Field label="Occupation / employment status">
        <select className={inputClass} disabled={readOnly} value={a.occupation} onChange={(e) => setField('occupation', e.target.value)}>
          <option value="">Select…</option>
          <option value="employed">Employed — salaried</option>
          <option value="selfemployed">Self-employed / business owner</option>
          <option value="cash">Cash-intensive business</option>
          <option value="student">Student</option>
          <option value="unemployed">Unemployed</option>
          <option value="retired">Retired</option>
          <option value="other">Other</option>
        </select>
      </Field>
      {occFlag && (
        <Notice kind="info">
          Occupation flagged by AUSTRAC guidance — confirm whether observed activity is consistent. SoF/SoW enquiry is <b>recommended</b>.
        </Notice>
      )}
      <Cond open={occFlag}>
        <Field label="Is activity consistent with stated occupation?" required>
          <div className="flex flex-wrap gap-2">
            <Chip name="occCons" value="yes" label="Yes" checked={a.occConsistent === 'yes'} disabled={readOnly} onChange={(v) => setField('occConsistent', v)} />
            <Chip name="occCons" value="no" label="No" checked={a.occConsistent === 'no'} disabled={readOnly} onChange={(v) => setField('occConsistent', v)} />
          </div>
        </Field>
      </Cond>
      <Field label="Customer type" hint="Feeds residual risk rating fields on the profile.">
        <select className={inputClass} disabled={readOnly} value={a.customerType} onChange={(e) => setField('customerType', e.target.value)}>
          <option value="">Select…</option>
          <option>Member — local</option>
          <option>Member — visiting</option>
          <option>Non-member / guest</option>
          <option>Temporary member</option>
        </select>
      </Field>
    </>
  )
}

function newSofRowId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

export function EcddSofFields({ formAnswers: a, onChange, readOnly, formsReadOnly, ecddId = null, patronId = '', patronLabel = '', onFormResponsesChange }) {
  const [evidenceDraft, setEvidenceDraft] = React.useState('')
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const foreignPep = isForeignPep(a)
  const sofReq = foreignPep ? 'yes' : a.sofRequired
  const refused = a.sofResp === 'refused'
  const evidenceItems = Array.isArray(a.sofEvidenceItems) ? a.sofEvidenceItems : []
  const sofDocs = Array.isArray(a.sofDocs) ? a.sofDocs : []

  const addEvidenceItem = () => {
    const text = evidenceDraft.trim()
    if (!text || readOnly) return
    setField('sofEvidenceItems', [
      ...evidenceItems,
      { id: newSofRowId('ev'), text, at: new Date().toISOString() },
    ])
    setEvidenceDraft('')
  }

  const removeEvidenceItem = (id) => {
    if (readOnly) return
    setField(
      'sofEvidenceItems',
      evidenceItems.filter((x) => x.id !== id)
    )
  }

  return (
    <>
      {ecddId && <EcddSowOutreachPanel ecddId={ecddId} readOnly={readOnly} />}
      {ecddId ? (
        <FormsModuleGate>
          <EcddFormsRail
            ecddId={ecddId}
            patronId={patronId}
            patronLabel={patronLabel}
            readOnly={formsReadOnly ?? readOnly}
            onAssignmentsChange={onFormResponsesChange}
          />
        </FormsModuleGate>
      ) : null}
      {foreignPep && (
        <Notice kind="block">Foreign PEP identified — SoF/SoW enquiry is <b>mandatory</b> and cannot be skipped.</Notice>
      )}
      <Field label="Is a SoF/SoW enquiry required for this case?" required hint="Mandatory for foreign PEPs. Risk-based otherwise.">
        <div className="flex flex-wrap gap-2">
          <Chip name="sofReq" value="yes" label="Yes" checked={sofReq === 'yes'} disabled={readOnly} onChange={(v) => setField('sofRequired', v)} />
          <Chip name="sofReq" value="no" label="No" checked={sofReq === 'no'} disabled={readOnly || foreignPep} onChange={(v) => setField('sofRequired', v)} />
        </div>
      </Field>
      <Cond open={sofReq === 'no'}>
        <Notice kind="info">SoF/SoW enquiry not required — evidence notes and documents stay closed. Record the reason below.</Notice>
        <Field label="Reason enquiry not required" required>
          <textarea className={`${inputClass} min-h-[72px] resize-y`} disabled={readOnly} value={a.sofNoReason} onChange={(e) => setField('sofNoReason', e.target.value)} placeholder="Document why SoF/SoW is not warranted — absence must be a recorded decision." />
        </Field>
      </Cond>
      <Cond open={sofReq === 'yes'}>
        <Field label="Enquiry method" required>
          <select className={inputClass} disabled={readOnly} value={a.sofMethod} onChange={(e) => setField('sofMethod', e.target.value)}>
            <option value="">Select…</option>
            <option>Conversation</option>
            <option>Written request</option>
            <option>Document request</option>
          </select>
        </Field>
        <Field label="Enquiry date" required>
          <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.sofDate} onChange={(e) => setField('sofDate', e.target.value)} />
        </Field>
        <Field label="Enquiry made by" required>
          <input className={inputClass} disabled={readOnly} value={a.sofBy} onChange={(e) => setField('sofBy', e.target.value)} placeholder="Name — role" />
        </Field>
        <Field label="Stated source of funds" required>
          <select className={inputClass} disabled={readOnly} value={a.sofCat} onChange={(e) => setField('sofCat', e.target.value)}>
            <option value="">Select…</option>
            <option>Salary / wages</option>
            <option>Business income</option>
            <option>Savings</option>
            <option>Gambling winnings</option>
            <option>Asset sale</option>
            <option>Inheritance</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Cond open={a.sofCat === 'other'}>
          <Field label="Detail" required>
            <input className={inputClass} disabled={readOnly} value={a.sofOtherDetail} onChange={(e) => setField('sofOtherDetail', e.target.value)} />
          </Field>
        </Cond>
        <Field label="Stated source of wealth">
          <select className={inputClass} disabled={readOnly} value={a.sowCat} onChange={(e) => setField('sowCat', e.target.value)}>
            <option value="">Select…</option>
            <option>Salary / wages</option>
            <option>Business income</option>
            <option>Savings</option>
            <option>Gambling winnings</option>
            <option>Asset sale</option>
            <option>Inheritance</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Evidence obtained" hint="Summary of what was sighted, plus discrete evidence notes and file attachments below.">
          <textarea className={`${inputClass} min-h-[64px] resize-y`} disabled={readOnly} value={a.sofEvidence} onChange={(e) => setField('sofEvidence', e.target.value)} placeholder="Structured description of evidence sighted (e.g. payslip sighted, business registration confirmed)." />
        </Field>

        <EcddSectionAttachments
          ecddId={ecddId}
          section="sof"
          docs={sofDocs}
          readOnly={readOnly}
          onChange={(next) => setField('sofDocs', next)}
          title="Supporting documents"
          hint="Attach payslips, bank statements, business docs, or other SoF/SoW evidence."
        />

        <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3.5 py-3 mb-4">
          <div className="text-[13px] font-semibold text-slate-700 mb-2">Evidence notes</div>
          <p className="text-xs text-slate-500 m-0 mb-2">Add each piece of evidence you obtained for this SoF/SoW enquiry. Use Screening refresh for OSINT / PEP checks.</p>
          {!readOnly && (
            <div className="flex flex-col sm:flex-row gap-2 mb-2">
              <input
                className={`${inputClass} flex-1`}
                value={evidenceDraft}
                onChange={(e) => setEvidenceDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addEvidenceItem()
                  }
                }}
                placeholder="e.g. Payslip dated 12/03/2026 sighted — employer Acme Pty Ltd"
              />
              <button
                type="button"
                onClick={addEvidenceItem}
                disabled={!evidenceDraft.trim()}
                className="shrink-0 px-3 py-2 text-xs font-semibold rounded-lg border border-psg-teal text-psg-teal bg-white hover:bg-psg-teal/5 disabled:opacity-40"
              >
                Add evidence
              </button>
            </div>
          )}
          {evidenceItems.length === 0 ? (
            <p className="text-xs text-slate-400 m-0">No evidence notes yet.</p>
          ) : (
            <ul className="m-0 p-0 list-none space-y-2">
              {evidenceItems.map((item) => (
                <li key={item.id} className="flex items-start justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2">
                  <span className="text-sm text-slate-700 whitespace-pre-wrap">{item.text}</span>
                  {!readOnly && (
                    <button type="button" className="text-xs text-red-600 hover:underline shrink-0 bg-transparent border-none cursor-pointer" onClick={() => removeEvidenceItem(item.id)}>
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <Field label="Patron response" required>
          <div className="flex flex-wrap gap-2">
            {[
              ['coop', 'Cooperative'],
              ['partial', 'Partial'],
              ['refused', 'Refused / evasive'],
            ].map(([v, l]) => (
              <Chip key={v} name="sofResp" value={v} label={l} checked={a.sofResp === v} disabled={readOnly} onChange={(val) => setField('sofResp', val)} />
            ))}
          </div>
        </Field>
        {refused && (
          <Notice kind="warn">Refusal or evasiveness is itself an indicator. A &quot;Satisfactory&quot; assessment requires an override justification.</Notice>
        )}
        <Field label="CO assessment" required>
          <div className="flex flex-wrap gap-2">
            {[
              ['sat', 'Satisfactory'],
              ['unsat', 'Unsatisfactory'],
              ['further', 'Requires further enquiry'],
            ].map(([v, l]) => (
              <Chip key={v} name="sofAssess" value={v} label={l} checked={a.sofAssess === v} disabled={readOnly} onChange={(val) => setField('sofAssess', val)} />
            ))}
          </div>
        </Field>
        <Cond open={refused && a.sofAssess === 'sat'}>
          <Field label="Override justification" required>
            <textarea className={`${inputClass} min-h-[64px] resize-y`} disabled={readOnly} value={a.satOverride} onChange={(e) => setField('satOverride', e.target.value)} placeholder="Explain why Satisfactory despite refused/evasive response." />
          </Field>
        </Cond>
      </Cond>
    </>
  )
}

export function EcddActivityFields({ formAnswers: a, onChange, readOnly, activityHelpers = null, ecddId = null }) {
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const { actMandatory, inconsistent, indicatorsSelected, noIndicators, noActivityToLink } =
    evaluateEcddFormGates(a)
  const actDocs = Array.isArray(a.actDocs) ? a.actDocs : []
  const toggleNoActivityToLink = () => {
    if (readOnly) return
    const next = !a.noActivityToLink
    onChange({
      noActivityToLink: next,
      actSummary: next ? '' : a.actSummary,
      actFrom: next ? '' : a.actFrom,
      actTo: next ? '' : a.actTo,
      actConsistent: next ? '' : a.actConsistent,
      inconNotes: next ? '' : a.inconNotes,
      indicators: next ? [] : a.indicators,
      noIndicators: false,
    })
  }
  const toggleIndicator = (value) => {
    if (readOnly) return
    const cur = Array.isArray(a.indicators) ? a.indicators : []
    if (value === NO_INDICATORS_VALUE) {
      const on = cur.includes(NO_INDICATORS_VALUE) || a.noIndicators
      onChange({
        indicators: on ? [] : [NO_INDICATORS_VALUE],
        noIndicators: !on,
      })
      return
    }
    const withoutNo = cur.filter((x) => x !== NO_INDICATORS_VALUE)
    onChange({
      indicators: withoutNo.includes(value)
        ? withoutNo.filter((x) => x !== value)
        : [...withoutNo, value],
      noIndicators: false,
    })
  }
  return (
    <>
      {activityHelpers}
      {actMandatory && (
        <label
          className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer mb-3 ${
            noActivityToLink ? 'border-psg-teal bg-[#0A516908]' : 'border-slate-200 bg-white'
          } ${readOnly ? 'opacity-60' : ''}`}
        >
          <input
            type="checkbox"
            className="accent-[#0A5169] mt-1"
            checked={noActivityToLink}
            disabled={readOnly}
            onChange={toggleNoActivityToLink}
          />
          <span>
            <span className="text-[13px] font-medium text-slate-700 block">No activity to link</span>
            <span className="text-xs text-slate-400">
              No gaming or payout activity to review for this ECDD — summary and consistency fields are not required.
            </span>
          </span>
        </label>
      )}
      <Cond open={!noActivityToLink}>
      <Field label="Review period">
        <div className="flex flex-wrap gap-2">
          <input type="date" className={`${inputClass} max-w-[200px]`} disabled={readOnly} value={a.actFrom} onChange={(e) => setField('actFrom', e.target.value)} />
          <input type="date" className={`${inputClass} max-w-[200px]`} disabled={readOnly} value={a.actTo} onChange={(e) => setField('actTo', e.target.value)} />
        </div>
      </Field>
      <Field label="Summary of activity reviewed" required={actMandatory && !noActivityToLink}>
        <textarea
          className={`${inputClass} min-h-[72px] resize-y`}
          disabled={readOnly}
          value={a.actSummary}
          onChange={(e) => {
            const v = e.target.value
            onChange({
              actSummary: v,
              noActivityToLink: v.trim() ? false : a.noActivityToLink,
            })
          }}
          placeholder="Turnover, payout pattern, session behaviour."
        />
      </Field>
      <EcddSectionAttachments
        ecddId={ecddId}
        section="activity"
        docs={actDocs}
        readOnly={readOnly}
        onChange={(next) => setField('actDocs', next)}
        title="Supporting documents"
        hint="Attach exported payout/session reports or other activity evidence."
      />
      <Field label="Consistent with stated SoF and profile?">
        <div className="flex flex-wrap gap-2">
          <Chip name="actCons" value="yes" label="Yes" checked={a.actConsistent === 'yes'} disabled={readOnly} onChange={(v) => setField('actConsistent', v)} />
          <Chip name="actCons" value="no" label="No" checked={a.actConsistent === 'no'} disabled={readOnly} onChange={(v) => setField('actConsistent', v)} />
        </div>
      </Field>
      <Cond open={inconsistent}>
        <Field label="Inconsistency notes" required>
          <textarea className={`${inputClass} min-h-[64px] resize-y`} disabled={readOnly} value={a.inconNotes} onChange={(e) => setField('inconNotes', e.target.value)} />
        </Field>
      </Cond>
      <Field label="Indicators observed" required={inconsistent && !noIndicators}>
        <div className="flex flex-col gap-1.5">
          {INDICATORS.map((ind) => {
            const on =
              ind.value === NO_INDICATORS_VALUE
                ? hasNoIndicatorsSelection(a)
                : Array.isArray(a.indicators) && a.indicators.includes(ind.value)
            return (
              <label
                key={ind.value}
                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer ${
                  on ? 'border-psg-teal bg-[#0A516908]' : 'border-slate-200 bg-white'
                } ${readOnly ? 'opacity-60' : ''}`}
              >
                <input
                  type="checkbox"
                  className="accent-[#0A5169] mt-1"
                  checked={on}
                  disabled={readOnly || (noIndicators && ind.value !== NO_INDICATORS_VALUE)}
                  onChange={() => toggleIndicator(ind.value)}
                />
                <span>
                  <span className="text-[13px] font-medium text-slate-700 block">{ind.label}</span>
                  <span className="text-xs text-slate-400">{ind.hint}</span>
                </span>
              </label>
            )
          })}
        </div>
      </Field>
      {indicatorsSelected && !noIndicators && (
        <Notice kind="warn">
          One or more indicators recorded — <b>Escalate → SMR</b> may be appropriate in the Decision section. Suspicion formation remains an explicit CO act in the SMR module.
        </Notice>
      )}
      </Cond>
    </>
  )
}

export function EcddEngageFields({ formAnswers: a, onChange, readOnly }) {
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const postSmr = a.triggerType === 'postsmr'
  return (
    <>
      <Field label="Was the patron engaged during this ECDD?" required>
        <div className="flex flex-wrap gap-2">
          <Chip name="engaged" value="yes" label="Yes" checked={a.engaged === 'yes'} disabled={readOnly} onChange={(v) => setField('engaged', v)} />
          <Chip name="engaged" value="no" label="No" checked={a.engaged === 'no'} disabled={readOnly} onChange={(v) => setField('engaged', v)} />
        </div>
      </Field>
      <Cond open={a.engaged === 'yes'}>
        <Field label="Engagement date" required>
          <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.engDate} onChange={(e) => setField('engDate', e.target.value)} />
        </Field>
        <Field label="Engaged by" required>
          <input className={inputClass} disabled={readOnly} value={a.engBy} onChange={(e) => setField('engBy', e.target.value)} placeholder="Name — role" />
        </Field>
        <Field label="Mode" required>
          <select className={inputClass} disabled={readOnly} value={a.engMode} onChange={(e) => setField('engMode', e.target.value)}>
            <option value="">Select…</option>
            <option>In person</option>
            <option>Phone</option>
            <option>Written</option>
          </select>
        </Field>
        <label className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border mt-2 cursor-pointer ${a.tipDecl ? 'border-psg-teal bg-[#0A516908]' : 'border-slate-200'}`}>
          <input type="checkbox" className="accent-[#0A5169] mt-1" checked={!!a.tipDecl} disabled={readOnly} onChange={(e) => setField('tipDecl', e.target.checked)} />
          <span>
            <span className="text-[13px] font-medium text-slate-700">Tipping-off declaration <span className="text-red-600">*</span></span>
            <span className="text-xs text-slate-400 block mt-0.5">
              I confirm that no disclosure of any suspicion, SMR, or intention to file an SMR was made to the patron or any third party during this engagement.
            </span>
          </span>
        </label>
      </Cond>
      {postSmr && (
        <>
          <Notice kind="block">Post-SMR trigger — tipping-off declaration is mandatory covering all patron contact during the ECDD period.</Notice>
          <label className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer ${a.tipDeclPostSmr ? 'border-psg-teal bg-[#0A516908]' : 'border-slate-200'}`}>
            <input type="checkbox" className="accent-[#0A5169] mt-1" checked={!!a.tipDeclPostSmr} disabled={readOnly} onChange={(e) => setField('tipDeclPostSmr', e.target.checked)} />
            <span>
              <span className="text-[13px] font-medium text-slate-700">Tipping-off declaration (post-SMR) <span className="text-red-600">*</span></span>
              <span className="text-xs text-slate-400 block mt-0.5">
                I confirm that no disclosure relating to the prior SMR or any ongoing suspicion has been made to the patron or any third party.
              </span>
            </span>
          </label>
        </>
      )}
    </>
  )
}

export function EcddScreeningFields({ formAnswers: a, onChange, readOnly, linkedScreeningNote = null }) {
  const setField = (key, value) => {
    if (readOnly) return
    onChange({ [key]: value })
  }
  const hit = a.scrResult === 'hit'
  return (
    <>
      <Notice kind="info">
        Run <b>AML / PEP</b> or <b>Screening sources</b> below — results are logged automatically. Record the CO outcome (date / Clear or Hit) here.
      </Notice>
      {linkedScreeningNote ? (
        <div className="mb-4 rounded-lg border border-psg-teal/25 bg-[#0A516908] px-3.5 py-2.5 text-[13px] text-psg-teal">
          {linkedScreeningNote}
        </div>
      ) : null}
      <Field label="Re-screen date" required>
        <input type="date" className={`${inputClass} max-w-[220px]`} disabled={readOnly} value={a.scrDate} onChange={(e) => setField('scrDate', e.target.value)} />
      </Field>
      <Field label="Result" required>
        <div className="flex flex-wrap gap-2">
          <Chip name="scrResult" value="clear" label="Clear" checked={a.scrResult === 'clear'} disabled={readOnly} onChange={(v) => setField('scrResult', v)} />
          <Chip name="scrResult" value="hit" label="Hit" checked={a.scrResult === 'hit'} disabled={readOnly} onChange={(v) => setField('scrResult', v)} />
        </div>
      </Field>
      <Cond open={hit}>
        <Field label="Match disposition" required>
          <div className="flex flex-wrap gap-2">
            <Chip name="scrDisp" value="false" label="False positive" checked={a.scrDisp === 'false'} disabled={readOnly} onChange={(v) => setField('scrDisp', v)} />
            <Chip name="scrDisp" value="true" label="True positive" checked={a.scrDisp === 'true'} disabled={readOnly} onChange={(v) => setField('scrDisp', v)} />
          </div>
        </Field>
        <Cond open={a.scrDisp === 'true'}>
          <Field label="PEP classification" required hint="Foreign PEP forces SoF/SoW enquiry and senior management approval via Approval Policies.">
            <div className="flex flex-wrap gap-2">
              {[
                ['domestic', 'Domestic'],
                ['foreign', 'Foreign'],
                ['intorg', 'International organisation'],
                ['notpep', 'Not a PEP (other list)'],
              ].map(([v, l]) => (
                <Chip key={v} name="pepClass" value={v} label={l} checked={a.pepClass === v} disabled={readOnly} onChange={(val) => setField('pepClass', val)} />
              ))}
            </div>
          </Field>
        </Cond>
      </Cond>
    </>
  )
}

export function EcddSignOffGate({ gates, allOk }) {
  const incomplete = (gates || []).filter((g) => !g.ok)
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5">
      <div className="text-[13px] font-bold text-[#073B4D] mb-1">
        {allOk ? 'Form complete' : 'Complete required sections'}
      </div>
      {!allOk && (
        <p className="text-xs text-slate-500 m-0 mb-2.5">
          Finish the items below, then you can record a decision.
        </p>
      )}
      <div className="space-y-1">
        {(allOk ? gates : incomplete).map((g) => (
          <div
            key={g.id}
            className={`flex items-center gap-2 text-[13px] py-0.5 ${g.ok ? 'text-green-700' : 'text-slate-600'}`}
          >
            <span
              className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                g.ok ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'
              }`}
            >
              {g.ok ? '✓' : '•'}
            </span>
            <span>{g.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function mergeFormAnswers(raw) {
  return { ...DEFAULT_ECDD_FORM_ANSWERS, ...(raw && typeof raw === 'object' ? raw : {}) }
}
